import { createContext, useContext, useCallback, useRef, useSyncExternalStore } from 'react';

/** Chunk a single array into multiple arrays, each containing `count` or fewer items
 */
import { jsx as _jsx } from "react/jsx-runtime";
const chunk = (array, count) => {
  if (count == null || count < 1) return [];
  const result = [];
  let i = 0;
  const length = array.length;
  while (i < length) {
    result.push(array.slice(i, i += count));
  }
  return result;
};
/**
 * By putting this in a getter, we esnure new objects are being made for each call
 */
const getLocalMutationsStoreDefaultState = () => ({
  visibleRowIndexes: {},
  visibleColumnIndexes: {}
});
function delayUntilIdle(callback, timeout) {
  if (window.requestIdleCallback) {
    window.requestIdleCallback(callback, {
      timeout
    });
    return;
  }
  window.setTimeout(callback, timeout);
}
const createVirtualizationStore = () => {
  const rowSubscribers = new Map();
  const columnSubscribers = new Map();
  let state = getLocalMutationsStoreDefaultState();
  const notify = ({
    rowIndexes,
    columnIndexes
  }) => {
    chunk(rowIndexes, 10).forEach(rowIndexChunk => {
      rowIndexChunk.forEach(rowIndex => {
        var _rowSubscribers$get;
        (_rowSubscribers$get = rowSubscribers.get(rowIndex)) === null || _rowSubscribers$get === void 0 || _rowSubscribers$get.forEach(sub => {
          delayUntilIdle(sub, 20);
        });
      });
    });
    chunk(columnIndexes, 10).forEach(columnIndexChunk => {
      columnIndexChunk.forEach(columnIndex => {
        var _columnSubscribers$ge;
        (_columnSubscribers$ge = columnSubscribers.get(columnIndex)) === null || _columnSubscribers$ge === void 0 || _columnSubscribers$ge.forEach(sub => {
          delayUntilIdle(sub, 20);
        });
      });
    });
  };
  return {
    getSnapshot() {
      return state;
    },
    subscribeByRowAndColumnIndexes({
      rowIndex,
      columnIndex,
      subscriber
    }) {
      if (!rowSubscribers.has(rowIndex)) {
        rowSubscribers.set(rowIndex, new Set());
      }
      if (!columnSubscribers.has(columnIndex)) {
        columnSubscribers.set(columnIndex, new Set());
      }
      rowSubscribers.get(rowIndex).add(subscriber);
      columnSubscribers.get(columnIndex).add(subscriber);
      return () => {
        rowSubscribers.get(rowIndex).delete(subscriber);
        columnSubscribers.get(columnIndex).delete(subscriber);
      };
    },
    reset() {
      const rowIndexes = Object.keys(state.visibleRowIndexes).map(Number);
      const columnIndexes = Object.keys(state.visibleColumnIndexes).map(Number);
      state = getLocalMutationsStoreDefaultState();
      notify({
        rowIndexes,
        columnIndexes
      });
    },
    updateVisibleIndexes: ({
      rows,
      columns
    }) => {
      state = {
        visibleRowIndexes: Object.assign({}, state.visibleRowIndexes, rows),
        visibleColumnIndexes: Object.assign({}, state.visibleColumnIndexes, columns)
      };
      notify({
        rowIndexes: Object.keys(rows).map(Number),
        columnIndexes: Object.keys(columns).map(Number)
      });
    }
  };
};
const VirtualizationContext = /*#__PURE__*/createContext(null);
export const ProvideVirtualization = ({
  children
}) => {
  const store = useRef(createVirtualizationStore());
  return /*#__PURE__*/_jsx(VirtualizationContext.Provider, {
    value: store.current,
    children: children
  });
};
const useVirtualizationStore = () => {
  const store = useContext(VirtualizationContext);
  if (store === null) {
    throw new Error('useVirtualizationStore must be used within a VirtualizationProvider');
  }
  return store;
};
export const useIsCellVisible = ({
  columnIndex,
  rowIndex
}) => {
  const store = useVirtualizationStore();
  const getSnapshot = useCallback(() => {
    const state = store.getSnapshot();
    return Boolean(state.visibleRowIndexes[rowIndex] && state.visibleColumnIndexes[columnIndex]);
  }, [columnIndex, rowIndex, store]);
  const subscribe = useCallback(onStoreChange => {
    return store.subscribeByRowAndColumnIndexes({
      columnIndex,
      rowIndex,
      subscriber: onStoreChange
    });
  }, [columnIndex, rowIndex, store]);
  const stateValue = useSyncExternalStore(subscribe, getSnapshot);
  if (rowIndex <= 10 && columnIndex <= 10) {
    return true;
  }
  return stateValue;
};
export const useUpdateVisibleIndexes = () => {
  const store = useVirtualizationStore();
  return store.updateVisibleIndexes;
};
export const useResetVisibleIndexes = () => {
  const store = useVirtualizationStore();
  return store.reset;
};